/**
 * TabBarInsetProvider — publishes how much of the bottom safe area the tab
 * navigator has ALREADY paid for, so screens stop paying it a second time.
 *
 * THE BUG THIS EXISTS TO KILL
 *
 * React Navigation's bottom-tabs does not overlay the tab bar. In
 * `BottomTabView`, the scene container and the bar are flex siblings in a
 * column, so a screen's bottom edge already stops ABOVE the bar. The bar is
 * sized `TAB_BAR_CONTENT_HEIGHT + insets.bottom` and pads itself by
 * `insets.bottom`, so the home-indicator / gesture-nav strip is fully spent by
 * the bar.
 *
 * `ScreenContainer` then spent `insets.bottom` a second time INSIDE the scene.
 * That second spend is a band of dead background between the end of the
 * scrollable area and the bar — 34px on an iPhone, 24-48px on Android — and
 * because it moves the scroll viewport's clip edge up, the last visible row is
 * sliced through the middle. Measured on 18 routes: the gap equalled
 * `insets.bottom` exactly, at every inset value tested.
 *
 * WHY THE RESERVATION IS DERIVED FROM `hideMobileTabBar`, NOT FROM
 * `BottomTabBarHeightContext`
 *
 * With `tabBarStyle: { display: 'none' }` React Navigation still reports a
 * height from that context — its `getTabBarHeight` fallback (49 + inset) —
 * until an onLayout corrects it. Keying the fix on that would flash the wrong
 * padding on the ~30 screens that legitimately hide the bar and DO own the
 * viewport bottom edge. `(tabs)/_layout.tsx` already knows the truth
 * synchronously, so it is the one that publishes it.
 *
 * Absent provider ⇒ 0, deliberately: a screen outside the tab navigator owns
 * its own bottom edge, and if this provider ever failed to mount the app would
 * degrade to the old behaviour rather than push content under the home
 * indicator.
 */
import React, { createContext, useContext } from 'react'

const TabBarInsetContext = createContext<number>(0)

interface TabBarInsetProviderProps {
  /** Total height the visible tab bar occupies, inset included. 0 when hidden. */
  reservation: number
  children: React.ReactNode
}

export function TabBarInsetProvider({ reservation, children }: TabBarInsetProviderProps) {
  return <TabBarInsetContext.Provider value={reservation}>{children}</TabBarInsetContext.Provider>
}

/** Bottom space the tab bar already occupies below the current screen. */
export function useTabBarInset(): number {
  return useContext(TabBarInsetContext)
}
